import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton, Tooltip, makeStyles, Typography } from '@ellucian/react-design-system/core';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import RepeatingSectionAddButton from './RepeatingSectionAddButton';
import { colorTextNeutral200, sizingXLarge, spacing30, spacing50, spacing20, spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { FieldArray } from '@nsr/react-form';

const useStyles = makeStyles(() => ({
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginLeft: 'auto',
        paddingTop: spacing40,
    },
    iconSpace: {
        marginLeft: spacing40,
    },
    repeatCount: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginLeft: 'auto',
    },
    root: {
        paddingTop: spacing40,
    },
    section: {
        background: colorTextNeutral200,
        padding: spacing40,
    },
    sectionContainer: {
        display: 'grid',
        gridGap: spacing50,
        gridTemplateRows: '1fr',
    },
    title: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridGap: spacing20,
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        height: sizingXLarge,
        marginBottom: spacing30,
    },
}));

const RepeatingSection = props => {
 
    const selectImportAdminMode = state => state.settings.importAdminMode;
    const isImportAdminMode = useSelector(selectImportAdminMode);
    
    const intl = useIntl();
    const {
        children,
        name,
        section,
    } = props;
    const classes = useStyles();
    const questionCount = useMemo(() => {
        return section.groups.reduce((memo, it) => {
            return memo + it.questions.length;
        }, 0);
    }, [section]);

    const newSectionRef = useRef(null);
    const [isAddAnotherClicked, setIsAddAnotherClicked] = useState(false);

    useEffect(() => {
        if (isAddAnotherClicked && newSectionRef.current) {
            newSectionRef.current.focus();
            setIsAddAnotherClicked(false);
        }
    }, [isAddAnotherClicked, newSectionRef.current]);

    const handleAddAnotherClick = useCallback(() => {
        setIsAddAnotherClicked(true);
    }, []);

    return (
        <div className={classes.root}>
            <FieldArray ensureAtLeastOneItem={!section.showRequiredRepeats && !section.requiredRepeats > 0} name={name} minimumItems={section.requiredRepeats} >
                {(name, state = [], add, remove) => (
                    <div className={classes.sectionContainer}>
                        {state.map((it, index) => {
                            const isLastItem = index === state.length - 1;
                            return (
                                <div key={index} className={classes.section} ref={isLastItem ? newSectionRef : null} tabIndex={isLastItem ? -1 : undefined}>
                                    <div className={classes.titleContainer}>
                                        {section.showRepetitionTitle && section.repetitionTitleId &&
                                            <Typography className={classes.title} component="div" variant="body2">                                                    
                                                <FormattedMessage id={section.repetitionTitleId} />
                                                <div>{index + 1}</div>
                                            </Typography>
                                        }
                                        {index > 0 && !isImportAdminMode &&  (index + 1 > section.requiredRepeats || !section.requiredRepeats) && 
                                            <div className={classes.repeatCount}>
                                                <Tooltip title={intl.formatMessage({ id: 'removeRepeatingSection' }, { count: questionCount })}>
                                                    <IconButton
                                                        color="secondary"
                                                        onClick={() => remove(index)}
                                                        className={classes.trashIcon}
                                                    >
                                                        <Icon name="trash"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        }
                                    </div>
                                    <div>
                                        {children(`${name}.${index}`)}
                                    </div>
                                </div>
                            );
                        })}
                        <div>
                            {!isImportAdminMode && section.showMaxRepetition && section.showRepetition && state.length < section.maxRepeats &&
                                <RepeatingSectionAddButton onClick={() => { add(); handleAddAnotherClick(); }} />
                            }
                            {!isImportAdminMode && !section.showMaxRepetition &&
                                <RepeatingSectionAddButton onClick={() => { add(); handleAddAnotherClick(); }} />
                            }
                        </div>
                    </div>
                )}
            </FieldArray>
        </div>
    );
};
RepeatingSection.propTypes = {
    children: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    section: PropTypes.object.isRequired,
};
export default RepeatingSection;
